import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  Checkbox,
  DatePicker,
  DatePickerInput,
  Tooltip,
  Tag,
  TextInput,
  Button
} from "@carbon/react";
import { Add, Information } from "@carbon/icons-react";
import CommonFields from "../../common/CommonField";

const RentalDetails = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 3,
  lastSavedStep = 0,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {
  console.log('🔍 RentalDetails (Step 3) received:', {
    formMode,
    currentStep,
    lastSavedStep,
    isEditForm: formMode === "edit" || lastSavedStep >= currentStep,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
    hasAttributes: !!data?.attributes,
    attributesKeys: data?.attributes ? Object.keys(data.attributes) : []
  });

  const isEditForm = useMemo(() => {
    return formMode === "edit" || lastSavedStep >= currentStep;
  }, [formMode, lastSavedStep, currentStep]);

  const FIELD_MAP = useMemo(() => ({
    expectedRent: "com_expected_rent",
    rentNegotiable: "com_rent_negotiable",
    maintenanceExtra: "com_maintenance_extra",
    maintenanceAmount: "com_monthly_maintenance",
    
    deposit: "com_deposit",
    depositNegotiable: "com_deposit_negotiable",
    leaseDuration: "com_lease_duration",
    lockinPeriod: "com_lockin_period",
    availableFrom: "com_available_from",
    idealFor: "com_ideal_for",
  }), []);

  // -----------------------------
  // STATE
  // -----------------------------
  const [form, setForm] = useState({
    expectedRent: "",
    rentNegotiable: "",
    maintenanceExtra: "",
    maintenanceAmount: "",
    deposit: "",
    depositNegotiable: "",
    leaseDuration: "",
    lockinPeriod: "",
    availableFrom: "",
    idealFor: [],
  });

  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [newTag, setNewTag] = useState("");

  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      return attributeValues
        .filter((a) => a.attrName === apiKey)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
        }));
    },
    [attributeValues, FIELD_MAP]
  );

  const requiredLabel = (text) => (
    <>
      {text} <span style={{ color: "#da1e28" }}>*</span>
    </>
  );

  // -----------------------------
  // FIELD UPDATES
  // -----------------------------
  const updateField = useCallback((key, value) => {
    console.log(`🔄 updateField: ${key} = ${value}`);
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  // -----------------------------
  // CHECKBOX HANDLERS - DEFINED BEFORE validationRules
  // -----------------------------
  const getBooleanValue = useCallback((fieldKey) => {
    const value = form[fieldKey];
    console.log(`🔘 getBooleanValue for ${fieldKey}:`, value);
    
    if (!value || value === "") return false;
    
    // Direct check for "true" or "false" strings
    if (value === "true" || value === true || value === "1") return true;
    if (value === "false" || value === false || value === "0") return false;
    
    // Check if the stored value corresponds to "Yes" option
    const options = getOptions(fieldKey);
    console.log(`🔘 Options for ${fieldKey}:`, options);
    
    const yesOption = options.find(opt => 
      String(opt.label).toLowerCase().includes("yes") || 
      String(opt.label).toLowerCase().includes("true") ||
      String(opt.label).toLowerCase() === "yes" ||
      String(opt.label).toLowerCase() === "true"
    );
    
    if (yesOption && String(value) === String(yesOption.id)) {
      console.log(`🔘 ${fieldKey} is YES (ID match)`);
      return true;
    }
    
    // Also check if value itself contains "yes" or "true"
    if (String(value).toLowerCase().includes("yes") || 
        String(value).toLowerCase().includes("true")) {
      console.log(`🔘 ${fieldKey} is YES (string contains)`);
      return true;
    }
    
    console.log(`🔘 ${fieldKey} is NO`);
    return false;
  }, [form, getOptions]);

  // -----------------------------
  // VALIDATION RULES - NOW getBooleanValue is defined
  // -----------------------------
  const validationRules = useMemo(() => ({
    expectedRent: {
      required: true,
      message: "Expected Rent is required",
      custom: (v) => {
        if (v && Number(v) <= 0) return "Expected Rent must be greater than 0";
        return null;
      }
    },
    deposit: {
      required: true,
      message: "Deposit is required",
      custom: (v) => {
        if (v && Number(v) <= 0) return "Deposit must be greater than 0";
        return null;
      }
    },
    leaseDuration: {
      required: false,
      message: "Lease Duration is required"
    },
    lockinPeriod: {
      required: false,
      message: "Lock-in Period is required"
    },
    maintenanceAmount: {
      required: false,
      custom: (v) => {
        if (getBooleanValue("maintenanceExtra") && (!v || v === "" || Number(v) <= 0)) {
          return "Maintenance Amount is required";
        }
        return null;
      }
    }
  }), [getBooleanValue]);

  // -----------------------------
  // LOAD INITIAL DATA (EDIT)
  // -----------------------------
  useEffect(() => {
    if (!data?.attributes) {
      console.log('💧 No attributes data found in RentalDetails');
      return;
    }

    console.log('💧 RentalDetails hydrating form with data:', data.attributes);

    const incoming = {};

    Object.entries(FIELD_MAP).forEach(([field, apiKey]) => {
      const raw = data.attributes[apiKey];
      if (raw == null) {
        console.log(`  - ${field}: No data for ${apiKey}`);
        return;
      }

      console.log(`  - ${field}: raw value =`, raw, 'type:', typeof raw);

      if (field === "idealFor") {
        // Handle array field
        if (Array.isArray(raw)) {
          incoming[field] = raw.map((x) => String(x));
        } else if (typeof raw === 'string') {
          // Try to parse JSON or comma-separated string
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              incoming[field] = parsed.map(x => String(x));
            } else {
              incoming[field] = raw.split(',').map(x => String(x.trim())).filter(x => x);
            }
          } catch {
            incoming[field] = raw.split(',').map(x => String(x.trim())).filter(x => x);
          }
        } else {
          incoming[field] = [String(raw)];
        }
      } else if (field === "expectedRent" || field === "maintenanceAmount" || field === "deposit") {
        // Convert numeric fields to string for input
        incoming[field] = String(raw);
      } else if (field === "availableFrom") {
        // Date field
        incoming[field] = String(raw);
      } else if (field === "rentNegotiable" || field === "maintenanceExtra" || field === "depositNegotiable") {
        // Boolean fields - convert to string
        incoming[field] = String(raw);
      } else {
        // All other dropdown fields - store as string
        incoming[field] = String(raw);
      }
    });

    console.log('💧 RentalDetails incoming form data:', incoming);
    setForm((prev) => {
      const newForm = { ...prev, ...incoming };
      console.log('💧 RentalDetails new form state:', newForm);
      return newForm;
    });
  }, [data, FIELD_MAP]);

  // -----------------------------
  // DEBUG CHECKBOX STATE
  // -----------------------------
  useEffect(() => {
    console.log('🔍 Checkbox states:', {
      rentNegotiable: {
        formValue: form.rentNegotiable,
        isChecked: getBooleanValue("rentNegotiable")
      },
      maintenanceExtra: {
        formValue: form.maintenanceExtra,
        isChecked: getBooleanValue("maintenanceExtra")
      },
      depositNegotiable: {
        formValue: form.depositNegotiable,
        isChecked: getBooleanValue("depositNegotiable")
      }
    });
  }, [form.rentNegotiable, form.maintenanceExtra, form.depositNegotiable, getBooleanValue]);

  const handleBooleanFieldChange = useCallback((fieldKey, checked) => {
    console.log(`🔘 handleBooleanFieldChange: ${fieldKey} = ${checked}`);
    const options = getOptions(fieldKey);
    
    if (options && options.length > 0) {
      // If we have predefined options, use them
      const yesOption = options.find(opt => 
        String(opt.label).toLowerCase().includes("yes") || 
        String(opt.label).toLowerCase().includes("true") ||
        String(opt.label).toLowerCase() === "yes" ||
        String(opt.label).toLowerCase() === "true"
      );
      
      const noOption = options.find(opt => 
        String(opt.label).toLowerCase().includes("no") || 
        String(opt.label).toLowerCase().includes("false") ||
        String(opt.label).toLowerCase() === "no" ||
        String(opt.label).toLowerCase() === "false"
      );
      
      if (checked && yesOption) {
        updateField(fieldKey, String(yesOption.id));
      } else if (!checked && noOption) {
        updateField(fieldKey, String(noOption.id));
      } else if (checked && options[0]) {
        // Default to first option if no explicit "yes"
        updateField(fieldKey, String(options[0].id));
      } else {
        updateField(fieldKey, "");
      }
    } else {
      // If no options available, use simple true/false
      updateField(fieldKey, checked ? "true" : "false");
    }
    
    // Mark field as touched
    setTouched(prev => ({ ...prev, [fieldKey]: true }));
  }, [getOptions, updateField]);

  const handleMaintenanceExtraChange = useCallback((checked) => {
    console.log(`🔘 Maintenance Extra changed: ${checked}`);
    handleBooleanFieldChange("maintenanceExtra", checked);
    if (!checked) {
      updateField("maintenanceAmount", "");
    }
  }, [handleBooleanFieldChange, updateField]);

  // -----------------------------
  // VALIDATION
  // -----------------------------
  const getFieldError = useCallback((field) => {
    const rule = validationRules[field];
    if (!rule) return null;

    const v = form[field];

    if (rule.required && (!v || v === "")) {
      return rule.message;
    }

    if (rule.custom) {
      return rule.custom(v);
    }

    return null;
  }, [form, validationRules]);

  const hasErrors = useCallback(() => {
    const fieldsWithErrors = Object.keys(validationRules).filter((field) => {
      const error = getFieldError(field);
      return error !== null;
    });
    
    console.log('🔍 Validation errors:', fieldsWithErrors);
    return fieldsWithErrors.length > 0;
  }, [getFieldError, validationRules]);

  // -----------------------------
  // TAG SYSTEM
  // -----------------------------
  const handleAddNewTag = useCallback(() => {
    if (newTag.trim()) {
      const newTagLabel = newTag.trim();
      setForm(prev => ({
        ...prev,
        idealFor: [...prev.idealFor, newTagLabel]
      }));
      setNewTag("");
    }
  }, [newTag]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddNewTag();
    }
  }, [handleAddNewTag]);

  const getAllTags = useCallback(() => {
    const backendOptions = getOptions("idealFor");
    const currentTags = form.idealFor || [];
    
    const allTags = [...backendOptions];
    
    // Add custom tags
    currentTags.forEach(tag => {
      if (typeof tag === 'string' && !allTags.some(t => t.label === tag)) {
        allTags.push({
          id: tag,
          label: tag,
          isCustom: true
        });
      }
    });
    
    return allTags;
  }, [getOptions, form.idealFor]);

  const { renderDropdown, renderNumberField } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur: (field) => setTouched(prev => ({ ...prev, [field]: true })),
    updateField,
    getOptions,
  });

  // -----------------------------
  // SAVE HANDLER
  // -----------------------------
  const handleSave = useCallback(async (navigateNext = false) => {
    console.log('💾 RentalDetails handleSave:', { 
      navigateNext, 
      isEditForm, 
      currentStep,
      hasPropertyUuid: !!propertyUuid,
      formData: form 
    });

    // Mark ALL fields as touched when saving
    const allFieldsTouched = Object.keys(validationRules).reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
    setTouched(allFieldsTouched);

    // Check for errors
    if (hasErrors()) {
      setError("Please fill all required fields");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      let uuid = propertyUuid;

      // Create property if it doesn't exist (should already exist from Step 1)
      if (!uuid && createProperty) {
        console.log('🆕 Creating property from Step 3...');
        uuid = await createProperty();
        onSaveSuccess?.(uuid, form, currentStep);
      }

      const payload = {};

      for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
        const v = form[field];
        if (v == null || v === "") continue;

        if (field === "idealFor") {
          if (Array.isArray(v) && v.length > 0) {
            payload[apiKey] = v;
          }
        } else if (field === "expectedRent" || field === "maintenanceAmount" || field === "deposit") {
          payload[apiKey] = Number(v);
        } else if (field === "availableFrom") {
          payload[apiKey] = v;
        } else if (field === "rentNegotiable" || field === "maintenanceExtra" || field === "depositNegotiable") {
        payload[apiKey] = getBooleanValue(field); 
      }
         else {
          // All dropdown fields - send as numbers
          payload[apiKey] = Number(v);
        }
      }

      console.log('💾 Saving rental details:', payload);
      await saveSectionAttributes(uuid, payload, form, "rental_details");

      setSuccess("Rental details saved successfully!");
      
      // Call parent's onSaveSuccess with step number
      onSaveSuccess?.(uuid, form, currentStep);
      
      // Call onComplete if provided
      onComplete?.(form);

      // Navigate to next step if requested
      if (navigateNext) {
        console.log('➡️ Navigating to next step after save');
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(err.message || "Failed to save rental details");
    } finally {
      setSaving(false);
    }
  }, [
    form,
    hasErrors,
    isEditForm,
    currentStep,
    propertyUuid,
    getBooleanValue,
    createProperty,
    onSaveSuccess,
    FIELD_MAP,
    saveSectionAttributes,
    onComplete,
    onNavigateNext,
    validationRules
  ]);

  const handleSaveOnly = useCallback((e) => {
    e?.preventDefault();
    handleSave(false);
  }, [handleSave]);

  const handleSaveAndNext = useCallback((e) => {
    //e?.preventDefault();
    handleSave(true);
  }, [handleSave]);

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          timeout={4000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          timeout={3000}
          lowContrast
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <Grid>
        <Column lg={16}>
          <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
            <Stack gap={6}>
              {/* ROW 1: Expected Rent & Maintenance */}

<div style={{ textAlign: "center", marginBottom: "1rem" }}>


  <p
    style={{
      color: "#0f62fe",
      fontSize: "0.95rem",
      fontWeight: 500,
      maxWidth: "700px",
      margin: "0 auto",
      lineHeight: "1.5",
    }}
  >
    Provide rental details about your property.
  </p>
</div>              <FormGroup>
                <Grid>
                  <Column sm={4} md={4} lg={8}>
                    <Tooltip
                      align="top"
                      label="Increase your chances of finding a tenant quickly, choose a competitive rent"
                    >
                      {renderNumberField("expectedRent", "Expected Rent", "Enter amount", true)}
                    </Tooltip>
                    
                    <Checkbox
                      id="rentNegotiable"
                      labelText="Rent Negotiable"
                      checked={getBooleanValue("rentNegotiable")}
                      onChange={(e) => handleBooleanFieldChange("rentNegotiable", e.target.checked)}
                      style={{ marginTop: '0.5rem' }}
                    />
                  </Column>
                  <Column sm={4} md={4} lg={8}>
                    <Checkbox
                      id="maintenanceExtra"
                      labelText="Maintenance Extra"
                      checked={getBooleanValue("maintenanceExtra")}
                      onChange={(e) => handleMaintenanceExtraChange(e.target.checked)}
                      style={{ marginBottom: getBooleanValue("maintenanceExtra") ? '1rem' : '0' }}
                    />
                    
                    {getBooleanValue("maintenanceExtra") && (
                      renderNumberField("maintenanceAmount", "Monthly Maintenance", "Enter amount", true)
                    )}
                  </Column>
                </Grid>
              </FormGroup>

              {/* ROW 2: Deposit, Lease Duration & Lock-in Period */}
              <FormGroup>
                <Grid>
                  <Column sm={4} md={4} lg={4}>
                    {renderNumberField("deposit", "Deposit", "Enter amount", true)}
                    
                    <Checkbox
                      id="depositNegotiable"
                      labelText="Deposit Negotiable"
                      checked={getBooleanValue("depositNegotiable")}
                      onChange={(e) => handleBooleanFieldChange("depositNegotiable", e.target.checked)}
                      style={{ marginTop: '0.5rem' }}
                    />
                  </Column>

                  <Column sm={4} md={4} lg={4}>
                    <Tooltip
                      align="top"
                      label="Lease duration is the duration for which you want to lease out your property"
                    >
                      {renderDropdown("leaseDuration", "Lease Duration (Years)", false)}
                    </Tooltip>
                  </Column>

                  <Column sm={4} md={4} lg={4}>
                    <Tooltip
                      align="top"
                      label="Lockin period is the minimum time that the tenant has to stay"
                    >
                      {renderDropdown("lockinPeriod", "Lockin Period (Years)", false)}
                    </Tooltip>
                  </Column>
                </Grid>
              </FormGroup>
{/* ROW 3: Available From */}
<FormGroup>
  <Grid>
    <Column sm={4} md={4} lg={8}>
      <TextInput
        id="availableFrom"
        type="date"
        labelText="Available From"
        value={form.availableFrom || ""}
        onChange={(e) => updateField("availableFrom", e.target.value)}
        onBlur={() => setTouched(prev => ({ ...prev, availableFrom: true }))}
      />
    </Column>
  </Grid>
</FormGroup>

              {/* ROW 4: Ideal For with Create New Tag */}
              <FormGroup>
                <Grid>
                  <Column sm={4} md={4} lg={8}>
                    <FormGroup legendText={
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <span>Ideal For</span>
                        <Tooltip
                          align="top"
                          label="Choose what is this property suited for by creating tags"
                        >
                          <Button
                            kind="ghost"
                            size="sm"
                            hasIconOnly
                            iconDescription="Information"
                            renderIcon={Information}
                            style={{ minWidth: "auto", padding: "0" }}
                          />
                        </Tooltip>
                      </div>
                    }>
                      {/* Create New Tag Input */}
                      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", alignItems: "flex-end" }}>
                        <TextInput
                          id="newTag"
                          labelText="Create New Tag"
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyPress={handleKeyPress}
                          placeholder="Enter new tag and press Enter or click Add"
                          style={{ flex: 1 }}
                        />
                        <Button
                          kind="secondary"
                          size="md"
                          renderIcon={Add}
                          onClick={handleAddNewTag}
                          disabled={!newTag.trim()}
                          style={{ marginBottom: "0.5rem" }}
                        >
                          Add
                        </Button>
                      </div>

                      {/* Tags Display */}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", padding: "0.25rem 0" }}>
                        {getAllTags().map((feature) => {
                          const isSelected = form.idealFor.some(tag => tag === feature.label);
                          return (
                            <Tag
                              key={feature.id}
                              type={isSelected ? "blue" : "gray"}
                              onClick={() => {
                                if (isSelected) {
                                  setForm(prev => ({
                                    ...prev,
                                    idealFor: prev.idealFor.filter(tag => tag !== feature.label)
                                  }));
                                } else {
                                  setForm(prev => ({
                                    ...prev,
                                    idealFor: [...prev.idealFor, feature.label]
                                  }));
                                }
                                setTouched(prev => ({ ...prev, idealFor: true }));
                              }}
                              style={{
                                cursor: "pointer",
                                padding: "0.5rem 1rem",
                                fontSize: "0.875rem",
                                border: isSelected ? "2px solid #0f62fe" : "2px solid transparent",
                              }}
                              title={feature.label}
                            >
                              {feature.label}
                            </Tag>
                          );
                        })}
                      </div>
                    </FormGroup>
                  </Column>
                </Grid>
              </FormGroup>

              {/* BUTTONS */}
              {CommonButtons && (
                <CommonButtons
                  onSave={handleSaveOnly}
                  onNext={handleSaveAndNext}
                  showSave
                  showNext
                  isSaveLoading={saving}
                  stepData={form}
                />
              )}
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default RentalDetails;